import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Radio, Users, Cross, MessageCircle, HelpCircle, Sparkles } from "lucide-react";
import { api, PLATFORM_LINKS } from "@/lib/api";
import LatestEpisodePlayer from "@/components/LatestEpisodePlayer";
import NewsletterForm from "@/components/NewsletterForm";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import ShowsGrid from "@/components/ShowsGrid";
import Quiz from "@/components/Quiz";
import { PARCOURS_LIST } from "@/lib/quiz-data";
import { TEAM } from "@/lib/team-data";

const STUDIO_TEAM = "/assets/studio-team.png";
const STUDIO_EMPTY = "/assets/studio-empty.png";

export default function Home() {
  const [latest, setLatest] = useState(null);

  useEffect(() => {
    api.get("/podcasts/latest").then((r) => setLatest(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="page-home" className="bg-[#F7F5F0]">
      {/* ============ HERO ============ */}
      <section className="relative min-h-[88vh] overflow-hidden bg-[#F7F5F0]">
        <div className="absolute inset-0">
          {/* Video banner */}
          <video
            className="absolute inset-0 w-full h-full object-cover"
            src="/assets/hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
          />
          {/* Soft veil so text stays readable — light, just enough for contrast */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-[#F7F5F0]/95" />
          <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#F7F5F0]/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-6 pt-32 pb-20 md:pt-40 text-center">
          <span className="jt-fadeUp inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-white/50 text-[#4A6D82] text-[11px] uppercase tracking-[0.25em] mb-6 jt-soft-shadow">
            <Cross className="w-3 h-3" strokeWidth={2.5} /> Podcasts & média chrétien
          </span>

          <h1 className="jt-fadeUp jt-delay-1 font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white mb-6" style={{textShadow: "0 2px 20px rgba(0,0,0,0.35)"}}>
            Le média qui <em className="not-italic text-[#F4E4C1]">encourage</em> ta foi.
          </h1>

          <p className="jt-fadeUp jt-delay-2 text-lg md:text-xl text-white max-w-2xl mx-auto mb-10 leading-relaxed" style={{textShadow: "0 2px 12px rgba(0,0,0,0.4)"}}>
            Jesus Talk t'accompagne pas à pas pour développer ta relation vivante avec Jésus, briser la routine religieuse et grandir dans la foi au quotidien.
          </p>

          <div className="jt-fadeUp jt-delay-3 flex flex-wrap items-center justify-center gap-3">
            <a
              href="#quiz"
              data-testid="hero-cta-quiz"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#4A6D82] text-white text-sm md:text-base hover:bg-[#2E4959] transition group jt-blue-glow"
            >
              <Sparkles className="w-4 h-4" /> Où en es-tu dans ta foi ?
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/podcasts"
              data-testid="hero-cta-podcast"
              className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/40 bg-white/15 backdrop-blur-md text-white text-sm md:text-base hover:bg-white/25 transition"
            >
              <Radio className="w-4 h-4" /> Écouter le dernier podcast
            </Link>
          </div>
        </div>
      </section>

      {/* ============ QUIZ ============ */}
      <section id="quiz" className="relative py-20 md:py-28 bg-[#F7F5F0]" data-testid="section-quiz">
        <div className="max-w-4xl mx-auto px-6">
          <Quiz />
        </div>
      </section>

      {/* ============ 4 PARCOURS ============ */}
      <section className="py-20 md:py-24 bg-white" data-testid="section-parcours">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82]">Suivi personnalisé</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1E2933] mt-3 leading-tight">
              4 parcours de croissance <em className="not-italic text-[#4A6D82]">par email</em>
            </h2>
            <p className="text-[#6B7A85] text-lg mt-4">
              Découvre lequel te correspond en faisant le quiz.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {PARCOURS_LIST.map((p) => (
              <div key={p.id} className={`relative overflow-hidden rounded-2xl p-6 bg-gradient-to-br ${p.color} text-white jt-soft-shadow`}>
                <p className="text-xs uppercase tracking-widest opacity-70 mb-2">Parcours {p.number}</p>
                <h3 className="font-serif text-2xl leading-tight mb-1">{p.title}</h3>
                <p className="text-sm italic text-white/80 mb-4">{p.subtitle}</p>
                <p className="text-xs text-white/80 mb-6 line-clamp-4 min-h-[64px]">{p.description}</p>
                <p className="text-[11px] uppercase tracking-widest text-white/70">✉ {p.emails} emails · gratuit</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 4 ÉMISSIONS ============ */}
      <section className="relative py-20 md:py-28 bg-[#14100B] text-[#F5EDDE] overflow-hidden" data-testid="section-shows">
        <div className="absolute inset-0 opacity-25 jt-brick" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <span className="text-xs uppercase tracking-[0.3em] text-[#F4A94E]">Nos programmes</span>
              <h2 className="font-display text-5xl md:text-7xl leading-none mt-3 tracking-wider">
                4 ÉMISSIONS.<br /><span className="text-[#E63946]">1 SEUL OBJECTIF.</span>
              </h2>
              <p className="text-white/70 text-lg mt-4 max-w-xl">
                Que tu sois en plein réveil spirituel ou en plein doute, Jesus Talk a un podcast pour toi.
              </p>
            </div>
            <Link to="/podcasts" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#E63946] text-white text-sm hover:bg-[#C1121F] transition self-start md:self-end">
              Explorer tous les épisodes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <ShowsGrid />
        </div>
      </section>

      {/* ============ LATEST EPISODE ============ */}
      {latest && (
        <section className="py-20 md:py-28 bg-[#F7F5F0]" data-testid="section-latest">
          <div className="max-w-7xl mx-auto px-6">
            <div className="max-w-3xl mb-10">
              <div className="flex items-center gap-2 mb-2">
                <span className="jt-wave-bar h-4" />
                <span className="jt-wave-bar h-4" style={{ animationDelay: "0.1s" }} />
                <span className="jt-wave-bar h-4" style={{ animationDelay: "0.2s" }} />
                <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82] ml-2">En ce moment</span>
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#1E2933] mt-3">
                Dernier épisode
              </h2>
            </div>
            <LatestEpisodePlayer episode={latest} light />
          </div>
        </section>
      )}

      {/* ============ ÉQUIPE PREVIEW ============ */}
      <section className="py-20 md:py-24 bg-white" data-testid="section-team-preview">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82]">L'équipe</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1E2933] mt-3 leading-tight">
              Trois voix, <em className="not-italic text-[#4A6D82]">un même appel</em>.
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {TEAM.map((m) => (
              <div key={m.id} className="group rounded-2xl overflow-hidden bg-[#F7F5F0] border border-[#DDE3E8] jt-soft-shadow">
                <div className="aspect-[4/5] relative overflow-hidden">
                  <img src={m.image} alt={m.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-white">
                    <p className="text-[10px] uppercase tracking-widest opacity-90 text-[#F4E4C1]">{m.role}</p>
                    <h3 className="font-serif text-2xl mt-1">{m.name}</h3>
                  </div>
                </div>
                <div className="p-5">
                  <p className="font-serif italic text-[#4A6D82] mb-2">« {m.tagline} »</p>
                  <p className="text-sm text-[#6B7A85]">{m.short}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/a-propos" className="inline-flex items-center gap-2 text-[#4A6D82] hover:text-[#2E4959] font-medium">
              En savoir plus sur notre vision <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ MINISTÈRE PREVIEW ============ */}
      <section className="relative py-20 md:py-28 overflow-hidden bg-[#2E4959] text-white" data-testid="section-ministry-preview">
        <div className="absolute inset-0">
          <img src="/assets/ministry/belver-pulpit.jpg" alt="Prophète Belver au pupitre" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#14100B]/90 via-[#2E4959]/80 to-[#4A6D82]/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <span className="text-xs uppercase tracking-[0.3em] text-[#F4E4C1]">Ministère prophétique et de réveil</span>
            <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] mt-3 mb-6">
              L'appel du réveil sous la <em className="not-italic text-[#F4E4C1]">puissance du Saint-Esprit</em>.
            </h2>
            <p className="text-white/85 text-lg mb-4">
              Au-delà du média chrétien Jesus Talk, nous répondons à l'appel de Dieu pour le Corps de Christ.
            </p>
            <p className="text-white/85 text-lg mb-8">
              Le Prophète Belver porte un ministère prophétique, accompagné de son épouse la servante de Dieu Laetitia. Ils se déplacent pour apporter la Parole de Dieu, la délivrance par la repentance et fortifier les assemblées sous la puissance du Saint-Esprit.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/ministere" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#2E4959] text-sm hover:bg-[#F4E4C1] transition">
                En savoir plus sur le ministère <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/ministere#section-invitation" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/40 text-white text-sm hover:bg-white/10 transition">
                Inviter dans notre église
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============ COMMUNITY ============ */}
      <section className="py-20 md:py-28 bg-[#F7F5F0]" data-testid="section-community">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-3xl mb-12">
            <span className="text-xs uppercase tracking-[0.3em] text-[#4A6D82]">Communauté</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1E2933] mt-3">
              Marchons ensemble.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <WhatsAppCTA />
            <NewsletterForm />
          </div>
        </div>
      </section>
    </div>
  );
}
